import { formatDate } from '@utils/dateHelpers';
import { useQuery } from '@tanstack/react-query';
import { ApiError, Speciality } from '@root/globalTypes';
import { queryKeys } from '@utils/consts';
import { queryFetch } from '@services/api';
import { WorkloadCalendarRespSuccess, WorkloadExtendedSpeciality } from './types';
import { useQueryParams } from '@hooks/queryParams';
import { parseSpecialties, camelToSnakeCase, snakeToCamelCase } from '@utils/textHelpers';
import { getFirstDayOfBusinessWeek } from '@contexts/workload/constants';

export const useGetWorkloadCalendar = (date: Date) => {
  const { params, parseParams } = useQueryParams();

  const { type, team, search, status, constructionTypes, unassigned } = params;

  const queryParams = parseParams({
    date: formatDate(getFirstDayOfBusinessWeek(date)),
    search,
    status,
    constructionTypes,
    team: team?.map((el) => camelToSnakeCase(el)),
    unassigned,
  });

  const { data, error, isFetching } = useQuery<WorkloadCalendarRespSuccess, ApiError>({
    queryKey: [...queryKeys.workloadWeeklyCalendar, queryParams],
    queryFn: () =>
      queryFetch({
        url: `workloads/calendar${queryParams}`,
      }),
    placeholderData: (previousData) => previousData as WorkloadCalendarRespSuccess,
    enabled: !type || type === 'week',
    select: (d) => ({
      ...d,
      records: d.records.map((el) => ({
        ...el,
        type: el.type ? (snakeToCamelCase(el.type) as WorkloadExtendedSpeciality) : el.type,
        foremen: el.foremen.map((foreman) => ({
          ...foreman,
          foremanSpecialties: parseSpecialties(
            foreman.foremanSpecialties || [],
            'camel',
          ) as Speciality[],
          schedules: foreman.schedules.map((schedule) => ({
            ...schedule,
            speciality: snakeToCamelCase(schedule.speciality) as WorkloadExtendedSpeciality,
          })),
        })),
        withoutTeam: el.withoutTeam.map((schedule) => ({
          ...schedule,
          speciality: snakeToCamelCase(schedule.speciality) as WorkloadExtendedSpeciality,
        })),
      })),
    }),
  });

  return {
    data,
    error,
    isLoading: isFetching,
  };
};
